import { useCnpjLookup } from '@/features/quotes/hooks/group-life/use-cnpj-lookup.hook';
import { useCaepfLookup } from '@/features/quotes/hooks/group-life/use-caepf-lookup.hook';
import {
  buildCaepfInactiveMessage,
  CAEPF_NOT_FOUND_MESSAGE,
  isActiveCaepf,
  resolveCaepfLookupOutcome,
  useCaepfSelection,
} from '@/features/quotes/hooks/common/use-caepf-selection.hook';
import { SelectCaepfModal } from '@/features/quotes/components/select-caepf.modal';
import {
  getCleanCpfCaepf,
  isValidCpfOrCaepf,
} from '@/features/quotes/utils/cpf-caepf-document.util';
import { formatGroupLifeDocument } from '@/features/quotes/utils/group-life/format';
import { MASK_PRESETS, MASK_PRESET_OPTIONS } from '@/shared/constants';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import {
  cleanCnpjOrCpf,
  useDebouncedValue,
  validateCaepf,
  validateCnpj,
  normalizeEmail,
} from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Building2, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { withMask } from 'use-mask-input';
import { CnaeCombobox } from './cnae-combobox.component';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    cnpj: string;
    legalName: string;
    cnaeCode?: string;
    /** true → documento é CAEPF (IdType 3); false → CNPJ (IdType 2). */
    isCaepf: boolean;
    /** CAEPF: CPF consultado (o `cnpj` carrega o CAEPF). */
    cpf?: string;
    /** Contato do subestipulante — opcionais, exibidos no resumo da cotação. */
    email?: string;
    phone?: string;
  }) => void;
  /** CNPJ/CAEPF do Estipulante (Step 1) — não pode ser reusado como
   *  subestipulante. */
  stipulatorDocument?: string;
  /** CNPJs/CAEPFs de subestipulantes já adicionados — bloqueia duplicidade. */
  existingSubStipulatorDocuments?: string[];
  /** Quando true, exibe spinner e desabilita o botão Adicionar — útil
   *  enquanto a API persiste o sub no InsureMO. */
  isSubmitting?: boolean;
};

export function AddSubStipulatorModal({
  isOpen,
  onClose,
  onConfirm,
  stipulatorDocument,
  existingSubStipulatorDocuments = [],
  isSubmitting = false,
}: Props) {
  const [isCaepf, setIsCaepf] = useState(false);
  const [document, setDocument] = useState('');
  // CAEPF: CPF consultado à parte (o `document` guarda o CAEPF escolhido).
  const [cpf, setCpf] = useState('');
  const [legalName, setLegalName] = useState('');
  const [cnaeCode, setCnaeCode] = useState('');
  // Contato: opcionais. Servem ao resumo da cotação, que mostra e-mail e
  // telefone de cada estipulação.
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  // Situação cadastral irregular do CAEPF — diferente de `lookupError` (falha
  // de consulta, que não impede o cadastro): esta BLOQUEIA o "Adicionar".
  const [caepfStatusError, setCaepfStatusError] = useState<string | null>(null);

  // Dispara a consulta (Brasil API p/ CNPJ, Compliance.Api p/ CAEPF) quando o
  // documento está completo e válido. Debounce 500ms (mesmo do Step 1).
  const cleanDoc = cleanCnpjOrCpf(document);
  // No modo CAEPF o campo aceita CPF (11 díg.) OU CAEPF (14 díg.): o CPF é o
  // ponto de entrada que descobre os CAEPFs vinculados ao titular.
  const isValidDoc = isCaepf
    ? isValidCpfOrCaepf(document)
    : cleanDoc.length === 14 && validateCnpj(document);
  const debouncedDoc = useDebouncedValue(isValidDoc ? cleanDoc : '', 500);
  const cnpjQuery = useCnpjLookup(
    !isCaepf && debouncedDoc ? debouncedDoc : undefined,
  );
  const caepfQuery = useCaepfLookup(
    isCaepf && debouncedDoc ? debouncedDoc : undefined,
  );
  const activeQuery = isCaepf ? caepfQuery : cnpjQuery;

  // Modal de seleção de CAEPF (empilhado sobre este). O documento gravado é
  // sempre a INSCRIÇÃO do CAEPF escolhido — o CPF é só o ponto de entrada.
  const caepfSelection = useCaepfSelection({
    onSelect: (entry, consultedCpf) => {
      setCpf(consultedCpf ?? '');
      if (entry.registrationNumber) {
        setDocument(formatGroupLifeDocument(entry.registrationNumber, true));
      }
      if (entry.responsibleName) {
        setLegalName(prev => prev || entry.responsibleName!);
      }
    },
  });
  const {
    requestSelection: requestCaepfSelection,
    resetFor: resetCaepfFor,
    close: closeCaepfSelection,
  } = caepfSelection;

  useEffect(() => {
    if (isCaepf) {
      if (caepfQuery.isError) {
        setLookupError(caepfQuery.error?.message ?? 'Erro ao consultar CAEPF');
        return;
      }
      setLookupError(null);

      if (caepfQuery.data) {
        const outcome = resolveCaepfLookupOutcome({
          cleanDocument: debouncedDoc,
          items: caepfQuery.data.items,
        });

        if (outcome === 'no-caepf') {
          setCaepfStatusError(null);
          setLookupError(CAEPF_NOT_FOUND_MESSAGE);
          return;
        }

        // CPF com CAEPF(s): um único preenche direto, vários abrem o modal.
        if (outcome === 'select') {
          setCaepfStatusError(null);
          requestCaepfSelection(debouncedDoc, caepfQuery.data.items);
          return;
        }

        // CAEPF (14 díg.) — inclusive o preenchido automaticamente a partir
        // de um CPF com um único CAEPF.
        setCaepfStatusError(
          caepfQuery.data.registrationStatus &&
            !isActiveCaepf(caepfQuery.data.registrationStatus)
            ? buildCaepfInactiveMessage(caepfQuery.data.registrationStatus)
            : null
        );
      }

      if (caepfQuery.data?.responsibleName) {
        setLegalName(prev => prev || caepfQuery.data!.responsibleName!);
      }
      return;
    }
    setCaepfStatusError(null);
    if (cnpjQuery.isError) {
      setLookupError(cnpjQuery.error?.message ?? 'Erro ao consultar CNPJ');
      return;
    }
    setLookupError(null);
    if (cnpjQuery.data) {
      setLegalName(prev => prev || cnpjQuery.data!.legalName);
      setCnaeCode(prev => prev || String(cnpjQuery.data!.primaryCnaeCode));
    }
  }, [
    isCaepf,
    debouncedDoc,
    cnpjQuery.data,
    cnpjQuery.isError,
    cnpjQuery.error,
    caepfQuery.data,
    caepfQuery.isError,
    caepfQuery.error,
    requestCaepfSelection,
  ]);

  const docLabel = isCaepf ? 'CPF/CAEPF' : 'CNPJ';
  const nameLabel = isCaepf ? 'Responsável' : 'Razão Social';

  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    const cleaned = cleanCnpjOrCpf(document);
    const stipulatorClean = cleanCnpjOrCpf(stipulatorDocument);
    const existingClean = existingSubStipulatorDocuments.map(d =>
      cleanCnpjOrCpf(d),
    );

    if (!document) e.document = `${docLabel} é obrigatório`;
    else if (isCaepf && getCleanCpfCaepf(document).length === 11)
      // CPF completo: aguarda a escolha do CAEPF no modal. O produto PME só
      // aceita CNPJ (2) ou CAEPF (3) como TipoDocumento.
      e.document = isValidCpfOrCaepf(document)
        ? 'Selecione o CAEPF vinculado a este CPF para continuar.'
        : 'CPF inválido';
    else if (isCaepf ? !validateCaepf(document) : !validateCnpj(document))
      e.document = `${docLabel} inválido`;
    else if (cleaned && cleaned === stipulatorClean)
      e.document = `Este ${docLabel} é o mesmo do Estipulante. Informe um documento diferente para o subestipulante.`;
    else if (cleaned && existingClean.includes(cleaned))
      e.document = `Já existe um subestipulante com este ${docLabel}.`;

    if (!legalName) e.legalName = `${nameLabel} é obrigatório`;

    // E-mail é opcional; só validamos o formato quando preenchido.
    const trimmedEmail = email.trim();
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      e.email = 'E-mail inválido';
    }
    return e;
  }, [
    document,
    legalName,
    email,
    isCaepf,
    docLabel,
    nameLabel,
    stipulatorDocument,
    existingSubStipulatorDocuments,
  ]);

  const reset = () => {
    setIsCaepf(false);
    setDocument('');
    setLegalName('');
    setCnaeCode('');
    setEmail('');
    setPhone('');
    setSubmitAttempted(false);
    setLookupError(null);
    setCaepfStatusError(null);
    resetCaepfFor('');
    // Fechar este modal fecha também o de seleção empilhado.
    closeCaepfSelection();
  };

  // Limpa o estado local quando o modal fecha (parent é dono do `isOpen`).
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  // Troca CNPJ ↔ CAEPF — limpa documento, nome, CNAE e erro de lookup (o
  // formato e a consulta mudam completamente).
  const toggleCaepf = () => {
    setIsCaepf(v => !v);
    setDocument('');
    setLegalName('');
    setCnaeCode('');
    setLookupError(null);
    setCaepfStatusError(null);
    resetCaepfFor('');
  };

  const handleConfirm = () => {
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) return;
    // Situação cadastral irregular impede o cadastro do subestipulante.
    if (caepfStatusError) return;
    onConfirm({
      // Envia o documento normalizado (sem máscara, maiúsculo) — CNPJ pode ser
      // alfanumérico; a API não deve receber a string mascarada.
      cnpj: cleanCnpjOrCpf(document),
      legalName,
      cnaeCode: cnaeCode || undefined,
      isCaepf,
      cpf: cpf || undefined,
      email: normalizeEmail(email) || undefined,
      phone: phone.trim() || undefined,
    });
  };

  const handleCancel = () => onClose();

  const showError = (key: string) =>
    submitAttempted ? errors[key] : undefined;

  return (
    <>
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[880px] max-w-[90vw] rounded-[24px] bg-[#F9F9F9] p-0 shadow-xl'>
          <div className='flex flex-col gap-4 p-6'>
            <Dialog.Header className='w-full p-0'>
              <Dialog.Title>
                <div className='flex items-center justify-between gap-3'>
                  <div className='flex items-center gap-2'>
                    <Building2 className='h-6 w-6 text-alba-black-500' />
                    <Text className='text-lg font-bold text-alba-black-500'>
                      Adicionar Estipulação
                    </Text>
                  </div>
                  <Dialog.CloseButton onClick={handleCancel} />
                </div>
              </Dialog.Title>
            </Dialog.Header>

            <Dialog.Body className='w-full p-0 space-y-4'>
              <Text className='text-sm text-alba-gray-600'>
                Dados do Estipulante
              </Text>

              {/* Toggle CNPJ ↔ CAEPF (mesmo padrão do Estipulante master). */}
              <div className='flex items-center gap-3'>
                <button
                  type='button'
                  role='switch'
                  aria-checked={isCaepf}
                  aria-label='CAEPF'
                  onClick={toggleCaepf}
                  className={`relative inline-flex h-5 w-10 shrink-0 items-center rounded-full p-[3px] transition-colors cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-alba-violet-300 focus:ring-offset-2 ${
                    isCaepf ? 'bg-alba-green-500' : 'bg-[#CFD2D3]'
                  }`}
                >
                  <span
                    className={`inline-block h-[14px] w-[14px] transform rounded-full bg-white shadow-sm transition-transform ${
                      isCaepf ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <Text className='text-sm font-semibold text-alba-black-500'>
                  CAEPF
                </Text>
              </div>

              <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
                <Field.Root
                  invalid={
                    !!showError('document') || !!lookupError || !!caepfStatusError
                  }
                >
                  <Field.Label>
                    {docLabel}
                    <span className='text-alba-error-red-500'>*</span>
                  </Field.Label>
                  <div className='relative'>
                    <Field.Input
                      key={isCaepf ? 'caepf' : 'cnpj'}
                      ref={withMask(
                        isCaepf ? MASK_PRESETS.cpfCaepf : MASK_PRESETS.cnpj,
                        // CNPJ alfanumérico entra sempre em maiúsculas.
                        isCaepf ? undefined : MASK_PRESET_OPTIONS.cnpj,
                      )}
                      value={document}
                      onChange={e => {
                        // Trocar o documento libera o modal de seleção.
                        resetCaepfFor(getCleanCpfCaepf(e.target.value));
                        setDocument(e.target.value);
                      }}
                      placeholder={
                        isCaepf
                          ? 'XXX.XXX.XXX-XX ou XXX.XXX.XXX/XXX-XX'
                          : 'XX.XXX.XXX/XXXX-XX'
                      }
                      maxLength={
                        isCaepf ? FIELD_MAX_LENGTHS.caepf : FIELD_MAX_LENGTHS.cnpj
                      }
                    />
                    {activeQuery.isFetching && (
                      <Loader2 className='absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-alba-gray-400' />
                    )}
                  </div>
                  {showError('document') && (
                    <Field.ErrorText>{showError('document')}</Field.ErrorText>
                  )}
                  {!showError('document') && caepfStatusError && (
                    <Field.ErrorText>{caepfStatusError}</Field.ErrorText>
                  )}
                  {!showError('document') && !caepfStatusError && lookupError && (
                    <Field.ErrorText>{lookupError}</Field.ErrorText>
                  )}
                </Field.Root>

                <Field.Root invalid={!!showError('legalName')}>
                  <Field.Label>
                    {nameLabel}
                    <span className='text-alba-error-red-500'>*</span>
                  </Field.Label>
                  <Field.Input
                    value={legalName}
                    onChange={e => setLegalName(e.target.value)}
                    placeholder={
                      isCaepf
                        ? 'Digite o nome do responsável'
                        : 'Digite o nome do estipulante'
                    }
                    maxLength={FIELD_MAX_LENGTHS.companyName}
                    disabled={activeQuery.isFetching}
                  />
                  {showError('legalName') && (
                    <Field.ErrorText>{showError('legalName')}</Field.ErrorText>
                  )}
                </Field.Root>
              </div>

              <CnaeCombobox value={cnaeCode} onChange={setCnaeCode} />

              {/* Contato — opcionais; aparecem no resumo da cotação. */}
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <Field.Root invalid={!!showError('email')}>
                  <Field.Label>E-mail</Field.Label>
                  <Field.Input
                    type='email'
                    value={email}
                    onChange={e => setEmail(e.target.value.toLowerCase())}
                    placeholder='Digite o e-mail'
                    maxLength={FIELD_MAX_LENGTHS.email}
                  />
                  {showError('email') && (
                    <Field.ErrorText>{showError('email')}</Field.ErrorText>
                  )}
                </Field.Root>

                <Field.Root>
                  <Field.Label>Telefone</Field.Label>
                  <Field.Input
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder='(00) 00000-0000'
                    ref={withMask(MASK_PRESETS.phone)}
                  />
                </Field.Root>
              </div>
            </Dialog.Body>

            <Dialog.Footer className='w-full p-0'>
              <div className='flex w-full items-center justify-end gap-3'>
                <Button
                  variant='ghost'
                  size='large'
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancelar
                </Button>
                <Button
                  variant='primary'
                  size='large'
                  onClick={handleConfirm}
                  disabled={
                    activeQuery.isFetching || isSubmitting || !!caepfStatusError
                  }
                >
                  {isSubmitting && <Loader2 className='w-4 h-4 animate-spin' />}
                  {isSubmitting ? 'Adicionando…' : 'Adicionar'}
                </Button>
              </div>
            </Dialog.Footer>
          </div>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>

    {/* Empilhado sobre este modal: seleção do CAEPF quando o documento
        informado é um CPF com CAEPFs vinculados. */}
    <SelectCaepfModal
      isOpen={caepfSelection.isOpen}
      onClose={caepfSelection.close}
      onConfirm={caepfSelection.confirm}
      items={caepfSelection.items}
    />
    </>
  );
}
